import { atpTitles, wnbaTitles, wtaTitles } from 'api/mockSportCategory';
import oddsApi from 'api/oddsApi';
import { useAppSelector } from 'app/hooks';
import axios, { CancelTokenSource } from 'axios';
import { selectBets, selectSearch, setGames } from 'features/home/homeSlice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setGameOdds,
  transformOddsResponseToGameOddsState,
  updateGameOddsResponse,
} from 'redux/gameSlice';
import { groupBetsToOddsResponse } from 'utils/betUtils';
export type GetGameParams = {
  status?: string | 'UPCOMING,LIVE';
  league: string;
};

const useGame = (filters: GetGameParams) => {
  const { status = 'UPCOMING,LIVE', league } = filters;
  const dispatch = useDispatch();
  const bets = useAppSelector(selectBets);
  const search = useSelector(selectSearch);

  const checkLeague = () => {
    if (league === 'WNBA') {
      return wnbaTitles;
    }
    if (league === 'ATP') {
      return atpTitles;
    }
    if (league === 'WTA') {
      return wtaTitles;
    } else {
      return league;
    }
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | null>(null);
  const [gameIds, setGameIds] = useState<string>('');

  const fetchGame = useCallback(async () => {
    const gameBetIds = bets.length
      ? bets
          .map((bet) => bet.gameId)
          .filter((value, index, self) => self.indexOf(value) === index)
          .join(',')
      : null;
    if (league) {
      const source = axios.CancelToken.source();
      setIsLoading(true);

      setCancelTokenSource(source);
      const params = {
        'status.in': status,
        'id.in': gameBetIds,
        sort: 'startDate,asc',
        size: 100,
        'main.equals': true,
        'isHidden.equals': false,

        'sportTitle.in': checkLeague(),
        ...(search.isSearchLayout && {
          'homeTeam.contains': search.searchText,
          'awayTeam.contains': search.searchText,
        }),
      };
      const { ok, body, error } = await oddsApi.getGames(params, source.token);
      if (ok && body) {
        dispatch(setGames(body));
        const gIds = body.map((game) => game.id).join(',');
        setGameIds(gIds);
      }
      if (error) {
        dispatch(setGames([]));
      }
      setIsLoading(false);
    }
  }, [search.searchText, status, league]);

  const fetchOdds = useCallback(async () => {
    if (gameIds) {
      const source = axios.CancelToken.source();
      const { ok, body } = await oddsApi.getOdds(
        {
          gameIds,
        },
        source.token,
      );
      if (ok && body) {
        dispatch(updateGameOddsResponse(transformOddsResponseToGameOddsState([...body])));
      }
    }
  }, [gameIds]);

  useEffect(() => {
    fetchOdds();
  }, [fetchOdds, dispatch]);

  useEffect(() => {
    dispatch(setGames(undefined));
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Request canceled due to new tab');
    }
    fetchGame();
  }, [dispatch, fetchGame]);

  useEffect(() => {
    if (!isLoading) {
      const idInterval = setInterval(() => {
        fetchGame();
      }, 5000);
      return () => {
        clearInterval(idInterval);
      };
    }
  }, [filters, isLoading]);

  return {
    isLoading,
  };
};

export default useGame;
